import jsQR from 'jsqr';
import { useEffect, useRef, useState } from 'react';

import { Boton, ErrorCaja } from './ui';
import { Iconos } from './iconos';

/**
 * Lee un QR con la cámara del aparato.
 *
 * ## Por qué un decodificador en JavaScript y no el del sistema
 *
 * Existe `BarcodeDetector`, que sería más rápido y no pesaría nada. Pero solo
 * está en Android, macOS y ChromeOS: en Chrome de Windows —donde se prueba el
 * portal— no existe, y entonces la función queda sin poder verificarse hasta
 * tenerla en una tablet. Un decodificador en JavaScript funciona en los dos
 * sitios por igual, así que se puede probar donde se escribe.
 *
 * ## Dónde no funciona, y se dice
 *
 * `getUserMedia` **exige contexto seguro**. El portal se sirve por HTTP plano,
 * así que ahí no existe y este componente lo dice en vez de fallar en silencio.
 * Dentro del APK sí funciona, porque Capacitor sirve la app desde
 * `https://localhost`, y la tablet de la caseta usa el APK — que es justo donde
 * hace falta.
 */
export function EscanerQr({
  onLeido,
  onCerrar,
  titulo = 'Apunta al código',
  ayuda,
}: {
  onLeido: (texto: string) => void;
  onCerrar: () => void;
  titulo?: string;
  ayuda?: string;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const lienzo = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `onLeido` en una referencia: si se guardara en las dependencias del efecto,
  // cada vez que la pantalla de arriba se redibuja se apagaría y encendería la
  // cámara. Se ve como un parpadeo y en algunos aparatos deja de enfocar.
  const alLeer = useRef(onLeido);
  alLeer.current = onLeido;

  useEffect(() => {
    // `vivo` corta el trabajo pendiente al desmontar: entre que se pide la
    // cámara y que el usuario la concede pueden pasar segundos, y para entonces
    // esta pantalla puede haberse cerrado.
    let vivo = true;
    let flujo: MediaStream | null = null;
    let cuadro = 0;

    async function arrancar() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          'Este navegador no puede abrir la cámara. En la app instalada sí funciona; ' +
            'aquí compara la huella a mano.',
        );
        return;
      }

      try {
        flujo = await navigator.mediaDevices.getUserMedia({
          // La de atrás: se apunta a una etiqueta pegada a un equipo, no a uno
          // mismo. Es `ideal` y no `exact` para que una tablet con una sola
          // cámara use la que tenga en vez de negarse.
          video: { facingMode: { ideal: 'environment' } },
        });
      } catch (e) {
        setError(
          e instanceof DOMException && e.name === 'NotAllowedError'
            ? 'No se dio permiso para la cámara.'
            : 'No se pudo abrir la cámara.',
        );
        return;
      }

      if (!vivo || !video.current) {
        flujo?.getTracks().forEach((t) => t.stop());
        return;
      }

      video.current.srcObject = flujo;
      await video.current.play().catch(() => undefined);
      cuadro = requestAnimationFrame(mirar);
    }

    function mirar() {
      if (!vivo) return;
      const v = video.current;
      const c = lienzo.current;

      // Los primeros cuadros llegan sin dimensiones, mientras la cámara
      // arranca. Leerlos daría un lienzo de 0×0 y una excepción por cuadro.
      if (v && c && v.videoWidth > 0) {
        // Se decodifica a resolución reducida. A tamaño completo cada cuadro
        // cuesta lo suficiente como para que el vídeo se vea a tirones, y un
        // QR de una etiqueta a un palmo se lee de sobra con esto.
        const escala = Math.min(1, 640 / v.videoWidth);
        c.width = Math.round(v.videoWidth * escala);
        c.height = Math.round(v.videoHeight * escala);

        const g = c.getContext('2d', { willReadFrequently: true });
        if (g) {
          g.drawImage(v, 0, 0, c.width, c.height);
          const imagen = g.getImageData(0, 0, c.width, c.height);
          // `dontInvert`: la etiqueta es oscura sobre claro, como todo QR
          // impreso. Buscar también el negativo dobla el trabajo por cuadro
          // para cubrir un caso que aquí no se da.
          const hallado = jsQR(imagen.data, imagen.width, imagen.height, {
            inversionAttempts: 'dontInvert',
          });
          if (hallado?.data) {
            alLeer.current(hallado.data);
            return;
          }
        }
      }
      cuadro = requestAnimationFrame(mirar);
    }

    void arrancar();

    return () => {
      vivo = false;
      cancelAnimationFrame(cuadro);
      // Apagar las pistas es lo que apaga la luz de la cámara. Sin esto la
      // tablet se queda con el testigo encendido hasta que alguien cierra la
      // app, y eso en una caseta se lee como que está grabando.
      flujo?.getTracks().forEach((t) => t.stop());
      if (video.current) video.current.srcObject = null;
    };
  }, []);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-headline-md text-on-surface">{titulo}</h1>
        {ayuda && <p className="mt-1 text-body-md text-on-surface-variant">{ayuda}</p>}
      </header>

      {error ? (
        <ErrorCaja>{error}</ErrorCaja>
      ) : (
        <div className="relative overflow-hidden rounded-xl bg-on-primary-container">
          <video
            ref={video}
            className="aspect-square w-full object-cover"
            playsInline
            muted
            // Sin esto iOS abre el vídeo a pantalla completa y tapa la
            // pantalla entera con su propio reproductor.
            autoPlay
          />
          {/* La guía. No recorta nada —se lee el cuadro completo—, solo le dice
              a quien apunta dónde poner la etiqueta. */}
          <div className="pointer-events-none absolute inset-[15%] rounded-lg border-2 border-white/70" />
        </div>
      )}

      <canvas ref={lienzo} className="hidden" />

      <Boton variante="texto" className="w-full" icono={Iconos.cerrar} onClick={onCerrar}>
        Cancelar
      </Boton>
    </div>
  );
}
